import React, { useState, useContext, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import IconButton from "@material-ui/core/IconButton";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import WbSunnyIcon from "@material-ui/icons/WbSunny";
import NightsStayIcon from "@material-ui/icons/NightsStay";
import InputAdornment from "@material-ui/core/InputAdornment";
import { Helmet } from "react-helmet";
import BRFlag from "../../assets/brazil.png";
import USFlag from "../../assets/unitedstates.png";
import ESFlag from "../../assets/esspain.png";
import ARFlag from "../../assets/arabe.png";
import infoChatIconMark from "../../assets/infoChatIconMark.svg";
import { getBackendUrl } from "../../config";
import packageJson from "../../../package.json";
import { createInfoChatLoginStyles } from "./infoChatLoginStyles";

const languageOptions = [
    { value: "pt-BR", label: "Português", icon: BRFlag },
    { value: "en", label: "English", icon: USFlag },
    { value: "es", label: "Spanish", icon: ESFlag },
    { value: "ar", label: "عربي", icon: ARFlag },
];

const useStyles = makeStyles((theme) => {
    const infoChatLogin = createInfoChatLoginStyles(theme);
    return ({
    root: {
        ...infoChatLogin.root,
        backgroundColor: infoChatLogin.page.backgroundColor,
    },

    shell: {
        ...infoChatLogin.shell,
    },

    institutionalPanel: {
        ...infoChatLogin.institutionalPanel,
    },

    customBgPhotoLayer: {
        ...infoChatLogin.customBgPhotoLayer,
    },

    customBgWashLayer: {
        ...infoChatLogin.customBgWashLayer,
    },

    institutionalPanelContent: {
        ...infoChatLogin.institutionalPanelContent,
    },

    logoBadge: {
        ...infoChatLogin.logoBadge,
        marginBottom: theme.spacing(4),
    },

    panelLogoImg: {
        width: "100%",
        maxWidth: "220px",
        height: "auto",
        maxHeight: "64px",
        display: "block",
        content: "url(" + theme.calculatedLogoDark() + ")",
    },

    institutionalHeadline: {
        ...infoChatLogin.institutionalHeadline,
        marginBottom: theme.spacing(2),
    },

    institutionalDescription: {
        ...infoChatLogin.institutionalDescription,
        maxWidth: "440px",
    },

    decorationWrap: {
        ...infoChatLogin.decorationWrap,
    },

    decorationCircle: {
        ...infoChatLogin.decorationCircle,
    },

    decorationImg: {
        ...infoChatLogin.decorationImg,
    },

    formPanel: {
        ...infoChatLogin.formPanel,
    },

    cardHeadline: {
        ...infoChatLogin.cardHeadline,
        textAlign: "left",
    },

    cardSubtitle: {
        ...infoChatLogin.cardSubtitle,
        marginBottom: theme.spacing(3),
        textAlign: "left",
    },

    form: {
        width: "100%",
    },

    submit: {
        margin: theme.spacing(3, 0, 2),
        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        color: "white",
        borderRadius: "12px",
        padding: "12px 0",
        fontSize: "16px",
        fontWeight: 600,
        textTransform: "none",
        boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
        border: "none",
        transition: "all 0.3s ease",
        "&:hover": {
            background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.dark})`,
            transform: "translateY(-2px)",
            boxShadow: `0 6px 20px ${theme.palette.primary.main}66`,
        },
        "&:active": {
            transform: "translateY(0)",
        },
    },

    // Controle de tema — sol + chave (trilho/thumb) + lua, um único alvo
    // clicável (mesmo padrão do botão de idioma: <button> simples, não
    // IconButton, para não herdar o formato circular do MUI).
    themeToggle: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 10px",
        borderRadius: "999px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        ...infoChatLogin.themeToggle,
        "&:hover": {
            transform: "scale(1.03)",
        },
    },

    themeToggleIcon: {
        fontSize: 16,
        display: "block",
        transition: "opacity 0.3s ease",
        ...infoChatLogin.themeToggleIcon,
    },

    themeToggleTrack: {
        position: "relative",
        width: 32,
        height: 16,
        borderRadius: "999px",
        flexShrink: 0,
        transition: "background-color 0.3s ease",
        ...infoChatLogin.themeToggleTrack,
    },

    themeToggleThumb: {
        position: "absolute",
        top: 1,
        width: 14,
        height: 14,
        borderRadius: "50%",
        transition: "left 0.2s ease",
        ...infoChatLogin.themeToggleThumb,
    },

    passwordToggleIcon: {
        ...infoChatLogin.passwordToggleIcon,
    },

    textField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            backgroundColor: infoChatLogin.card.backgroundColor,
            transition: "all 0.3s ease",
            "& fieldset": {
                borderColor: infoChatLogin.borderSubtleColor,
            },
            "&:hover fieldset": {
                borderColor: `${infoChatLogin.brandPrimaryColor}66`,
            },
            "&.Mui-focused fieldset": {
                borderColor: infoChatLogin.brandPrimaryColor,
                borderWidth: "2px",
            },
        },
        "& .MuiInputLabel-root": {
            color: infoChatLogin.cardSubtitle.color,
            fontWeight: 500,
            "&.Mui-focused": {
                color: infoChatLogin.brandPrimaryColor,
            },
        },
    },

    topControls: {
        ...infoChatLogin.topControls,
    },

    languageSelector: {
        borderRadius: "12px",
        padding: "8px 12px",
        ...infoChatLogin.languageSurface,
    },

    registerLink: {
        textDecoration: "none",
        fontWeight: 600,
        transition: "all 0.3s ease",
        ...infoChatLogin.registerLinkColor,
        "&:hover": {
            textDecoration: "underline",
        },
    },

    languageDropdown: {
        display: "flex",
        alignItems: "center",
        background: "none",
        border: "none",
        color: "inherit",
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer",
        gap: "8px",
        transition: "opacity 0.3s ease",
        "&:hover": {
            opacity: 0.8,
        },
    },

    languageOptions: {
        position: "absolute",
        top: "100%",
        left: "0",
        marginTop: "8px",
        borderRadius: "12px",
        padding: "8px",
        zIndex: 1000,
        minWidth: "140px",
        ...infoChatLogin.languageOptionsSurface,
    },

    languageOption: {
        background: "none",
        border: "none",
        color: "inherit",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        width: "100%",
        padding: "8px 12px",
        textAlign: "left",
        borderRadius: "8px",
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
            opacity: 0.75,
        },
    },

    flagIcon: {
        width: 20,
        height: 15,
        borderRadius: 2,
    },

    versionInfo: {
        borderRadius: "8px",
        padding: "6px 12px",
        fontSize: "12px",
        fontWeight: 500,
        opacity: 0.8,
        transition: "opacity 0.3s ease",
        ...infoChatLogin.versionInfoSurface,
        "&:hover": {
            opacity: 1,
        },
    },
})});

const Login = () => {
    const classes = useStyles();
    const { colorMode } = useContext(ColorModeContext);
    const { appLogoFavicon, appName, mode } = colorMode;
    const [user, setUser] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [allowSignup, setAllowSignup] = useState(false);
    const { getPublicSetting } = useSettings();
    const { handleLogin } = useContext(AuthContext);

    const [open, setOpen] = useState(false);
    const ref = useRef();
    const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
    const [backgroundLight, setBackgroundLight] = useState("");
    const [backgroundDark, setBackgroundDark] = useState("");

    const getCompanyIdFromUrl = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const companyId = urlParams.get("companyId");
        return companyId ? parseInt(companyId) : null;
    };

    const handleChangeInput = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value });
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handlSubmit = (e) => {
        e.preventDefault();
        handleLogin(user);
    };

    useEffect(() => {
        const companyId = getCompanyIdFromUrl();

        getPublicSetting("userCreation", companyId)
            .then((data) => {
                setAllowSignup(data === "enabled");
            })
            .catch((error) => {
                console.log("Error reading setting", error);
            });

        getPublicSetting("enabledLanguages", companyId)
            .then((langs) => {
                let arr = ["pt-BR", "en"];
                try {
                    if (langs) arr = JSON.parse(langs);
                } catch {}
                setEnabledLanguages(arr);
            })
            .catch(() => {
                setEnabledLanguages(["pt-BR", "en"]);
            });

        getPublicSetting("appLogoBackgroundLight", companyId)
            .then((bgLight) => {
                if (bgLight) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + bgLight;
                    setBackgroundLight(fullUrl);
                } else {
                    setBackgroundLight("");
                }
            })
            .catch((err) => {
                console.error("Erro ao carregar imagem de fundo clara:", err);
                setBackgroundLight("");
            });

        getPublicSetting("appLogoBackgroundDark", companyId)
            .then((bgDark) => {
                if (bgDark) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + bgDark;
                    setBackgroundDark(fullUrl);
                } else {
                    setBackgroundDark("");
                }
            })
            .catch((err) => {
                console.error("Erro ao carregar imagem de fundo escura:", err);
                setBackgroundDark("");
            });
    }, []);

    const current =
        languageOptions.find((opt) => opt.value === i18n.language) ||
        languageOptions[0];

    const handleSelect = (opt) => {
        i18n.changeLanguage(opt.value);
        localStorage.setItem("language", opt.value);
        setOpen(false);
        window.location.reload();
    };

    const hasValidLightBg = backgroundLight && backgroundLight.trim() !== '';
    const hasValidDarkBg = backgroundDark && backgroundDark.trim() !== '';
    const hasCustomBackground = mode === "light" ? hasValidLightBg : hasValidDarkBg;
    const customBackgroundUrl = mode === "light" ? backgroundLight : backgroundDark;

    // O fundo azul-marinho integral da PÁGINA (`classes.root`) é obrigatório
    // e nunca configurável — a imagem de fundo customizada por empresa não
    // substitui mais o fundo inteiro da página (violaria a composição
    // aprovada). Em vez disso, quando configurada, a foto aparece só DENTRO
    // do painel institucional (a área de marca à esquerda), como duas
    // camadas sobrepostas (`customBgPhotoLayer` + `customBgWashLayer`
    // translúcido por cima) — nunca cobrindo o cartão inteiro nem a metade
    // branca do formulário. A foto só entra como `url(...)` puro no style
    // inline (nunca combinada a um gradiente ali): o parser CSS usado nos
    // testes rejeita silenciosamente `linear-gradient(...)` atribuído via
    // `element.style`, embora funcione normalmente em navegadores reais.

    return (
        <>
            <Helmet>
                <title>{appName || "AtendeChat"}</title>
                <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
            </Helmet>

            <CssBaseline />

            <div className={classes.root}>
                {/* Idioma e tema, agrupados no topo direito da página */}
                <div className={classes.topControls}>
                    <div ref={ref} className={classes.languageSelector}>
                        <button
                            onClick={() => setOpen((o) => !o)}
                            className={classes.languageDropdown}
                        >
                            <img
                                src={current.icon}
                                alt={current.label}
                                className={classes.flagIcon}
                            />
                            {current.label}
                            <span>▾</span>
                        </button>

                        {open && (
                            <div className={classes.languageOptions}>
                                {languageOptions
                                    .filter((opt) => enabledLanguages.includes(opt.value))
                                    .map((opt) => (
                                        <button
                                            key={opt.value}
                                            onClick={() => handleSelect(opt)}
                                            className={classes.languageOption}
                                        >
                                            <img
                                                src={opt.icon}
                                                alt={opt.label}
                                                className={classes.flagIcon}
                                            />
                                            {opt.label}
                                        </button>
                                    ))}
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        className={classes.themeToggle}
                        onClick={colorMode.toggleColorMode}
                        aria-label="toggle theme"
                    >
                        <WbSunnyIcon
                            className={classes.themeToggleIcon}
                            style={{ opacity: mode === "dark" ? 0.35 : 1 }}
                        />
                        <span className={classes.themeToggleTrack}>
                            <span
                                className={classes.themeToggleThumb}
                                style={{ left: mode === "dark" ? 18 : 2 }}
                            />
                        </span>
                        <NightsStayIcon
                            className={classes.themeToggleIcon}
                            style={{ opacity: mode === "dark" ? 1 : 0.35 }}
                        />
                    </button>
                </div>

                <div className={classes.shell}>
                    {/* Painel institucional — informativo (título e descrição
                        continuam acessíveis a leitores de tela); só o círculo
                        decorativo é ignorado. Ocultado por CSS abaixo de "sm",
                        nunca duplicado. */}
                    <div
                        className={classes.institutionalPanel}
                        data-testid="institutional-panel"
                    >
                        {hasCustomBackground && (
                            <div
                                className={classes.customBgPhotoLayer}
                                style={{ backgroundImage: `url(${customBackgroundUrl})` }}
                                data-testid="institutional-panel-photo"
                            />
                        )}
                        {hasCustomBackground && (
                            <div className={classes.customBgWashLayer} />
                        )}

                        <div className={classes.institutionalPanelContent}>
                            <div className={classes.logoBadge}>
                                <img className={classes.panelLogoImg} alt="" />
                            </div>

                            <Typography variant="h4" className={classes.institutionalHeadline}>
                                {i18n.t("login.modernization.panelHeadline")}
                            </Typography>
                            <Typography variant="body1" className={classes.institutionalDescription}>
                                {i18n.t("login.modernization.panelSubtitle")}
                            </Typography>

                            <div className={classes.decorationWrap} aria-hidden="true">
                                <div className={classes.decorationCircle}>
                                    <img
                                        src={infoChatIconMark}
                                        alt=""
                                        className={classes.decorationImg}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Formulário — mesmo componente em qualquer largura, nunca duplicado */}
                    <div className={classes.formPanel}>
                        <Typography variant="h5" className={classes.cardHeadline}>
                            {i18n.t("login.modernization.formTitle")}
                        </Typography>
                        <Typography variant="body2" className={classes.cardSubtitle}>
                            {i18n.t("login.modernization.formSubtitle", { appName: appName || "AtendeChat" })}
                        </Typography>

                        <form className={classes.form} noValidate onSubmit={handlSubmit}>
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label={i18n.t("login.form.email")}
                                name="email"
                                value={user.email}
                                onChange={handleChangeInput}
                                autoComplete="email"
                                autoFocus
                                className={classes.textField}
                            />
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label={i18n.t("login.form.password")}
                                type={showPassword ? "text" : "password"}
                                id="password"
                                value={user.password}
                                onChange={handleChangeInput}
                                autoComplete="current-password"
                                className={classes.textField}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={togglePasswordVisibility}
                                                edge="end"
                                                className={classes.passwordToggleIcon}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                className={classes.submit}
                            >
                                {i18n.t("login.buttons.submit")}
                            </Button>
                            {allowSignup && (
                                <Grid container justifyContent="center">
                                    <Grid item>
                                        <Link
                                            href="#"
                                            variant="body2"
                                            component={RouterLink}
                                            to="/signup"
                                            className={classes.registerLink}
                                        >
                                            {i18n.t("login.buttons.register")}
                                        </Link>
                                    </Grid>
                                </Grid>
                            )}
                        </form>
                    </div>
                </div>

                {/* Informação da versão */}
                <Typography className={classes.versionInfo}>
                    v{packageJson.version}
                </Typography>
            </div>
        </>
    );
};

export default Login;
